import { Button } from '@/components/ui/button'
import { Check, ChevronDown, Star } from 'lucide-react'
import { useEffect, useState } from 'react'
import imageSkillTracks from '@/images/skilltracks/skilltracks_logo.svg'
import imageEngineBelt from '@/images/skilltracks/engine_belt.png'
import imageGearBlue from '@/images/skilltracks/gear_blue.png'
import imageWorkers from '@/images/skilltracks/workers.png'
import imageEngineGray from '@/images/skilltracks/engine_gray.png'
import laptop1 from '@/images/skilltracks/laptop1.png'
import laptop2 from '@/images/skilltracks/laptop2.png'
import laptop3 from '@/images/skilltracks/laptop3.png'
import laptop4 from '@/images/skilltracks/laptop4.png'
import laptop5 from '@/images/skilltracks/laptop5.png'
import laptop6 from '@/images/skilltracks/laptop6.png'
import { useIsUSD } from '@/hooks/useIsUSD'

const SkillTracks = () => {
    const [openFaqIndex, setOpenFaqIndex] = useState(-1)
    const isUSD = useIsUSD()

    const faqs = [
        {
            question: "What's included with a SkillTracks subscription?",
            answer:
                'A SkillTracks subscription gives you access to structured, role-based learning tracks designed to build real technician skills. Each track includes curated online courses, progress tracking, and clear skill pathways to help technicians develop efficiently and consistently.',
        },
        {
            question: 'Can I complete a skills assessment without any assigned learning?',
            answer:
                'Yes. SkillTracks allows you to complete a skills assessment on its own. This will help you understand your current skill levels before assigning any learning or building a training plan.',
        },
        {
            question: 'How does SkillTracks answer my training needs?',
            answer:
                'SkillTracks uses skills assessments and defined learning paths to align training with your technicians\' actual needs. Instead of one-size-fits-all courses, training is targeted, measurable, and scalable across your team.',
        },
        {
            question: 'Can I reassign seats?',
            answer:
                'Yes. SkillTracks seats can be reassigned as staffing needs change, making it easy to onboard new technicians or adjust training without purchasing a new subscription.',
        },
        {
            question: 'Does the SkillTracks subscription include in-person learning?',
            answer:
                'Yes. SkillTracks includes in-person learning. As we bring back in-person training, availability will depend on local demand. If in-person training is available in your area, you can attend for free.',
        },
        {
            question: 'How do I convert my existing CTI subscription into a SkillTracks subscription?',
            answer:
                'If you\'re an existing CTI subscriber, our team can help transition your account to SkillTracks. Simply contact CTI support (ctisupport@carquest.com), and we\'ll guide you through the conversion process.',
        },
    ]

    return (
        <div className="flex flex-col lg:container px-4 lg:px-12 shadow-md bg-gray-50">
            <div className="py-12 sm:py-16 lg:py-16 w-full flex justify-center items-center">
                <div
                    style={{ backgroundImage: `url(${imageEngineBelt})` }}
                    className={`relative px-4 sm:px-6 border-2 rounded
                    border-foreground w-full max-w-7xl
                    py-8 sm:py-10 lg:py-10
                    bg-cover bg-center bg-no-repeat`}
                >
                    <div className="text-center mb-10 sm:mb-10 lg:mb-10">
                        <p className="text-lg sm:text-xl lg:text-3xl font-bold mb-4">
                            Introducing the New Way to Learn
                        </p>
                        <div className="w-full flex justify-center">
                            <img
                                src={imageSkillTracks}
                                alt="SkillTracks overview"
                                className="
                w-full
                sm:w-4/5
                lg:w-2/3
                max-w-4xl
                h-auto
                object-contain
                rounded
                "
                            />
                        </div>
                    </div>

                    <div className="flex flex-col w-full lg:flex-row justify-center gap-4 lg:gap-10 mb-10 lg:mb-10">
                        {[
                            ['Technician Skill', 'Assessment'],
                            ['Customized', 'Learning Paths'],
                            ['Constant Skill', 'Improvement'],
                        ].map(([a, b]) => (
                            <div
                                key={a}
                                className="
                    bg-background bg-gray-300 border-2 border-foreground
                    px-4 sm:px-6 py-3 rounded text-center
                    w-full sm:max-w-[60%] lg:max-w-none
                    mx-auto
                "
                            >
                                <p className="font-bold text-lg sm:text-xl lg:text-3xl">{a}</p>
                                <p className="font-bold text-lg sm:text-xl lg:text-3xl">{b}</p>
                            </div>
                        ))}
                    </div>

                    <p className="text-lg sm:text-xl lg:text-3xl font-bold text-center mb-10 lg:mb-20">
                        Learning paths include content from all of our subscriptions.
                    </p>

                    {/* Pricing */}
                    <div className="flex flex-col lg:flex-row justify-center gap-4 lg:gap-0
                            w-full lg:absolute lg:left-1/2 lg:-translate-x-1/2 items-center
                            lg:-bottom-20">

                        {/* First learner */}
                        <div
                            className="bg-background border-2 border-foreground
                        w-full sm:max-w-[60%] lg:max-w-[40%] px-6 py-4 text-center rounded-3xl lg:rounded-l-[150px] lg:rounded-r-none lg:border-r-0 bg-gray-300"
                        >
                            <div className="flex items-end justify-center">
                                <p className="text-3xl sm:text-5xl lg:text-8xl font-semibold">$100</p>
                                <p className="text-base sm:text-xl lg:text-3xl font-bold ml-1 mb-1">
                                    {!isUSD && <span className="text-xxl mr-1">USD</span>}
                                    /month
                                </p>
                            </div>
                            <p className="text-lg sm:text-xl lg:text-3xl font-bold">
                                for your first learner
                            </p>
                        </div>

                        {/* Additional learners */}
                        <div
                            className="bg-background border-2 border-foreground
                       w-full sm:max-w-[60%] lg:max-w-[40%] px-6 py-4 text-center rounded-3xl lg:rounded-r-[150px] lg:rounded-l-none bg-gray-300"
                        >
                            <div className="flex items-end justify-center">
                                <p className="text-3xl sm:text-5xl lg:text-8xl font-semibold">$50</p>
                                <p className="text-base sm:text-xl lg:text-3xl font-bold ml-1 mb-1">
                                {!isUSD && <span className="text-xxl mr-1">USD</span>}
                                /month
                                </p>
                            </div>
                            <p className="text-lg sm:text-xl lg:text-3xl font-bold">
                                per additional learner
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className='flex justify-center gap-4 sm:gap-20 lg:gap-20 lg:mt-28'>
                <button
                  disabled
                  className="bg-primary text-white text-3xl font-bold px-6 py-3 rounded
                            opacity-50 cursor-not-allowed"
                >
                    Log in
                </button>
                <button
                  disabled
                  className="bg-secondary text-white text-3xl font-bold px-6 py-3 rounded
                            opacity-50 cursor-not-allowed"
                >
                    Sign up
                </button>
            </div>

            {/* Dual Learning Section */}
            <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-200 mt-12 lg:mt-24">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 lg:mb-16">
                        Level Up Skills with Dual Learning
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
                        {/* Self-Paced */}
                        <div className="border-2 border-foreground rounded overflow-hidden bg-white/70">
                            <div className="bg-primary text-white p-5 text-center font-bold text-xl sm:text-2xl lg:text-4xl">
                                SELF-PACED <br className="hidden lg:block" /> eLEARNING
                            </div>

                            <div className="p-4 sm:p-6 lg:p-8">
                                <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-center lg:text-left mb-4 lg:mb-6">
                                    Self-paced, online technical training.
                                </p>

                                <ul className="space-y-2 sm:space-y-3 lg:space-y-4">
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            Online training modules
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            Video on demand courses
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            ASE test prep
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Virtual Classroom */}
                        <div className="border-2 border-foreground rounded overflow-hidden bg-white/70">
                            <div className="bg-secondary text-white p-5 text-center font-bold text-xl sm:text-2xl lg:text-4xl">
                                VIRTUAL <br className="hidden lg:block" /> CLASSROOM
                            </div>

                            <div className="p-4 sm:p-6 lg:p-8">
                                <p className="text-lg text-center lg:text-left  sm:text-xl lg:text-2xl font-semibold mb-4 lg:mb-6">
                                    Live streamed, instructor-led technical training.
                                </p>

                                <ul className="space-y-2 sm:space-y-3 lg:space-y-4">
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            Interactive learning
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            Live demonstrations
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            Shared tool screens
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-xl">•</span>
                                        <span className="text-base sm:text-lg lg:text-xl">
                                            Chat with instructors with real-world experience
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Skills Assessment Section */}
            <section className="py-12 sm:py-16 lg:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Remove the Guesswork with Skill Assessments
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-0 items-center">
                        <div style={{ backgroundImage: `url(${laptop1})` }} className={`relative w-full aspect-[4/3]
                            bg-cover bg-center bg-no-repeat rounded-xl`}>
                        </div>

                        <div className="w-full text-center md:text-left">

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                It’s hard to know what each technician knows,
                                which courses are right, and how to track
                                progress over time. SkillTracks solves this with
                                online skill assessments, an easy-to-read
                                dashboard, and simple learning assignments.
                            </p>

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                Our platform assesses technician skills, displays
                                progress in a clear dashboard, and makes it easy
                                to assign targeted learning paths.
                            </p>
                            <br></br>
                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                Blended learning combines on-demand digital
                                training with live demonstrations, so technicians
                                can learn at their own pace and apply their
                                learning in the shop.
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            <hr className="border-t-2 border-primary" />

            <section className="py-12 sm:py-16 lg:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto">-
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Target Training for Your Shop Needs
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-0 items-center">
                        <div className="w-full text-center md:text-left">

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                Every shop’s needs are different, and a one-size-fits-
                                all training approach rarely delivers the results your
                                team needs. SkillTracks is adaptable and
                                customized for your shop, your technicians, and
                                the skills that matter most to your daily operations.
                            </p>

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                SkillTracks lets managers focus development where
                                it will have the greatest impact. Instead of clunky,
                                generic programs, technicians follow learning paths
                                that support the work they perform every day.
                            </p>
                            <br></br>
                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                As skills grow in the areas that matter most, your
                                team becomes more confident, productivity
                                improves, and training becomes a clear driver of
                                shop success rather than just another thing to do.
                            </p>
                        </div>

                        <div style={{ backgroundImage: `url(${laptop2})` }} className={`relative w-full aspect-[4/3]
                            bg-cover bg-center bg-no-repeat rounded-xl`}>
                        </div>

                    </div>
                </div>
            </section>

            <hr className="border-t-2 border-primary" />

            <section className="py-12 sm:py-16 lg:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Track Your Progress
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-0 items-center">
                        <div style={{ backgroundImage: `url(${laptop3})` }} className={`relative w-full aspect-[4/3]
                            bg-cover bg-center bg-no-repeat rounded-xl`}>
                        </div>

                        <div className="w-full text-center md:text-left">

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                SkillTracks helps you see your progress clearly, so
                                you always know which skills you’ve mastered and
                                what to work on next.
                            </p>

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                You can track your training, follow your learning
                                path, and build new skills at a pace that fits your
                                daily work.
                            </p>
                            <br></br>
                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                Your progress reflects real growth in the shop,
                                helping you gain confidence, improve performance,
                                and move forward in your career performance.
                            </p>
                        </div>

                    </div>
                </div>
            </section>


            {/* Automotive Training */}
            <section style={{ backgroundImage: `url(${imageGearBlue})` }} className={`relative rounded bg-cover bg-center bg-no-repeat text-white py-10 pb-20 sm:pb-10 md:pb-10 lg:pb-20 lg:p-20 px-4 sm:px-6 lg:px-8`}>
                <div className="max-w-full mx-auto justify-start flex flex-col">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-semibold text-center mb-8 sm:mb-12">
                        Simplify Your Automotive Training
                    </h2>
                    <br />
                    <div className="flex flex-row justify-center sm:justify-around items-center gap-3 md:gap-12 mb-12 xs:mb-0">
                        <div className="text-center">
                            <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
                                Assess Skills
                            </p>
                        </div>

                        <div className="text-center">
                            <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
                                Target Training
                            </p>
                        </div>

                        <div className="text-center">
                            <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
                                See Progress
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 sm:py-16 lg:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Designed for Shop Leadership
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-0 items-center">

                        {/* Left Content */}
                        <div className="w-full text-center md:text-left">

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                SkillTracks is designed so shop owners and managers can oversee training
                                without being counted as a user. Managers can assign learning, track progress,
                                and understand skill gaps across the team, all from one place.
                            </p>

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                We designed SkillTracks to adapt with you.
                                Easily swap users in and out of training as your
                                team evolves without getting a headache.
                            </p>
                            <br></br>
                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                With limited seat counts, you stay in control.
                                When someone completes a course, changes
                                roles, or leaves the team, simply reassign that
                                seat to someone new. No more paying for
                                unused licenses. No more rigid contracts.
                            </p>
                        </div>

                        {/* Right Mockup */}
                        <div style={{ backgroundImage: `url(${laptop4})` }} className={`relative w-full aspect-[4/3]
                            bg-cover bg-center bg-no-repeat rounded-xl`}>
                        </div>
                    </div>
                </div>
            </section>

            <hr className="border-t-2 border-primary" />

            <section className="py-12 sm:py-16 lg:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        We’ll Get You Ready for ASE
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-0 items-center">

                        <div style={{ backgroundImage: `url(${laptop5})` }} className={`relative w-full aspect-[4/3]
                            bg-cover bg-center bg-no-repeat rounded-xl`}>
                        </div>

                        <div className="w-full text-center md:text-left">

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                ASE certification is a big step in your automotive
                                journey. SkillTracks makes sure you’re prepared to
                                pass.
                            </p>

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground leading-relaxed">
                                Our structured learning paths, targeted practice,
                                and real-world case studies help technicians build
                                the skills and confidence they need to succeed.
                                No guesswork. Just focused preparation that
                                gets results.
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            <hr className="border-t-2 border-primary" />

            <section className="py-12 sm:py-16 lg:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-full mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Explore Our Extensive Training Catalog
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-0 items-center">

                        <div className="w-full text-center md:text-left">

                            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                Access 2,966 professionally built courses that power
                                your automotive journey cover essential topics like:
                            </p>

                            <ul className="list-disc pl-6 text-left text-base sm:text-lg lg:text-xl xl:text-2xl text-foreground mb-4 sm:mb-6 leading-relaxed">
                                <li>Air and Fuel Management</li>
                                <li>Axles and Driveline</li>
                                <li>Battery, Starting, and Charging</li>
                                <li>Brakes</li>
                                <li>Cooling System</li>
                                <li>Electrical and Networking</li>
                                <li>Emission Controls</li>
                                <li>Engine Mechanical</li>
                                <li>Heating and A/C</li>
                                <li>Hybrid and EV</li>
                                <li>Ignition</li>
                                <li>Steering, Suspension, and Alignment</li>
                                <li>Valve Train and Timing</li>
                            </ul>

                        </div>

                        <div style={{ backgroundImage: `url(${laptop6})` }} className={`relative w-full aspect-[4/3]
                            bg-cover bg-center bg-no-repeat rounded-xl`}>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ratings Section */}
            <section className="bg-gray-200 rounded py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl lg:max-w-4xl mx-auto text-center">

                    {/* Stars */}
                    <div className="flex justify-center gap-1 sm:gap-2 mb-6 sm:mb-8">
                        {[...Array(5)].map((_, i) => (
                            <Star
                                key={i}
                                className="fill-red-600 text-red-600
                            w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12"
                            />
                        ))}
                    </div>

                    {/* Quote */}
                    <blockquote>
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed mb-4 sm:mb-6">
                            “I can not express enough how great CTI training is. I know what I know today
                            and have found a lot of resources along the way because of CTI training.”
                        </p>

                        <footer className="text-sm sm:text-base md:text-lg lg:text-xl">
                            <strong className="block sm:inline">– Matthew Schrepel</strong>
                            <span className="block sm:inline text-muted-foreground italic sm:ml-2">
                                West Valley Auto
                            </span>
                        </footer>
                    </blockquote>

                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-12 sm:py-16 lg:py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-3 sm:space-y-4 max-w-6xl mx-auto">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="border-b border-gray-400 shadow-md border-foreground rounded overflow-hidden"
                            >
                                {/* Question */}
                                <button
                                    onClick={() =>
                                        setOpenFaqIndex(openFaqIndex === index ? -1 : index)
                                    }
                                    className="
                    w-full px-4 sm:px-6 md:px-8 py-4 sm:py-5
                    flex items-start justify-between text-left
                    bg-white hover:bg-gray-100 transition
                    font-bold text-foreground
                    active:scale-[0.99]
                    "
                                >
                                    <span
                                        className="
                        text-base sm:text-lg md:text-xl lg:text-2xl
                        w-full pr-4 text-left
                    "
                                    >
                                        {faq.question}
                                    </span>

                                    <ChevronDown
                                        className={`
                        flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 border border-black rounded-full p-0.5
                        transition-transform duration-300 ease-in-out bg-gray-100
                        ${openFaqIndex === index ? 'rotate-180' : ''}
                    `}
                                    />
                                </button>

                                {/* Answer (animated) */}
                                <div
                                    className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${openFaqIndex === index
                                            ? 'max-h-screen opacity-100'
                                            : 'max-h-0 opacity-0'}
                    `}
                                >
                                    <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6 bg-background text-foreground">
                                        <p
                                            className="
                        text-sm sm:text-base md:text-lg lg:text-xl
                        font-semibold leading-relaxed text-center
                        "
                                        >
                                            {faq.answer}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Test Drive Section */}
            <section className="bg-gray-200 rounded">
                <div className="w-full mx-auto">
                    <div className="flex md:flex-row flex-col mb-12 md:mb-0 gap-12 items-center">
                        {/* Left Illustration Placeholder */}
                        <div style={{ backgroundImage: `url(${imageWorkers})` }} className={`relative md:w-[50%] w-full md:h-[500px] h-[250px] sm:h-[300px] bg-cover bg-center bg-no-repeat`}>
                        </div>

                        {/* Right Content */}
                        <div className="md:w-[50%] xs:w-full text-center md:text-left flex flex-col justify-center items-center px-3">
                            <h2 className="text-3xl text-center md:text-5xl font-bold mb-6">
                                Take a Test Drive
                            </h2>
                            <p className="text-2xl md:text-3xl text-center mb-8 leading-relaxed">
                                See the difference with SkillTracks. Clear learning paths and skills-based training built for real technicians.
                            </p>
                            <div className="bg-secondary cursor-pointer rounded text-2xl md:text-3xl center hover:bg-secondary/85 text-white font-bold px-8 py-3">
                                Explore SkillTracks
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Helpful Videos Section */}
            <section className="py-12 sm:py-16 lg:py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto flex flex-col items-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 sm:mb-14 lg:mb-16">
                        Helpful Videos
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-3 gap-6 lg:gap-8 w-full">
                        {[
                            'Skill Assessments',
                            'Taking Classes',
                            'Assigning Courses',
                        ].map((title, index) => (
                            <div
                                key={title}
                                className={`
                    bg-blue-700 hover:bg-blue-800 transition
                    text-white rounded p-6 sm:p-8
                    aspect-[4/3] flex flex-col justify-between
                    items-center text-center cursor-pointer
                    sm:col-span-2
                    ${index === 2 ? 'sm:col-start-2 lg:col-start-auto' : ''}
                    lg:col-span-1
                `}
                            >
                                {/* Video thumbnail placeholder */}
                                <div className="w-full h-full flex-1 rounded-lg bg-white/10 mb-4" />

                                <p className="text-lg sm:text-xl lg:text-2xl font-bold">
                                    {title}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final Section */}
            <section style={{ backgroundImage: `url(${imageEngineGray})` }} className={`bg-cover bg-center bg-no-repeat
                        py-16 sm:py-20 lg:py-28
                        px-4 sm:px-6 lg:px-8`}>
                <div className="max-w-6xl mx-auto text-center">
                    <div className="mb-10 sm:mb-12">
                        <div className="w-full flex justify-center">
                            <img
                                src={imageSkillTracks}
                                alt="SkillTracks overview"
                                className="
                    w-full
                    sm:w-4/5
                    lg:w-2/3
                    max-w-4xl
                    h-auto
                    object-contain
                    rounded-xl
                    "
                            />
                        </div>
                    </div>

                    <button
                        className="bg-secondary hover:bg-red-700 transition
                        text-white font-bold
                        text-lg sm:text-xl lg:text-2xl
                        px-6 sm:px-10 py-3 sm:py-4
                        rounded-lg
                        w-full sm:w-auto"
                    >
                        Start Learning
                    </button>
                </div>
            </section>
        </div>
    )
}
export default SkillTracks